import { CustomFieldDataType, CustomListItemStyle } from 'shared'
import { Row, Col, Input, Checkbox, DatePicker, InputNumber, Form } from 'antd'
import MetadataListFormItems from './MetadataListFormItems'
import { CustomFieldRowProps } from './types'
import { useProjectCustomFieldsContext } from '../../ProjectCustomFieldsContext'
import CIMSSelect from '../../../CIMSSelect'
import { useOptions } from '../../../CIMSSelect/hooks/useOptions'
import { CIMSFormItemProps } from '../../../CIMSFormItem/types'
import CIMSFormItem from '../../../CIMSFormItem'
import { useDisabledOptions } from '../../../../hooks/useDisabledOptions'

const CustomFieldRow: React.FC<CustomFieldRowProps> = ({
  customField,
  alreadyDisabled,
}) => {
  const {
    description,
    name,
    id,
    customFieldToClassType: { required, alwaysVisible },
    customListItems,
    dataTypeId,
    metadata,
    deleted,
  } = customField
  const { disabledById } = useProjectCustomFieldsContext()
  const [disabledOptions, { some: disabled }] = useDisabledOptions(
    disabledById({ customFieldId: id, alreadyDisabled })
  )
  const formItem: CIMSFormItemProps = {
    label: name,
    help: description,
    name: ['customFields', id, 'value'],
    requiredMessage: required ? `Please enter a value for ${name}` : undefined,
    disabled: disabledOptions,
    valuePropName:
      dataTypeId === CustomFieldDataType.Boolean.id ? 'checked' : undefined,
  }
  const input = { disabled, allowClear: true }
  const customListItemOptions = customListItems?.map(({ id, value }) => ({
    value: id,
    label: value,
  }))
  const options = useOptions({
    items: customListItems,
    value: 'id',
    label: 'value',
    sort: 'value',
  })
  if (deleted && !alwaysVisible) return null
  /**
   * don't be tempted to remove the various hidden Form.Item components
   *
   * even if their initial values will be set by the fromCustomFields method in
   * the provider, we still need to set them here
   *
   * doing otherwise breaks the validation logic
   */
  return (
    <Row key={id} gutter={16} justify="start">
      <Col span={24}>
        <Form.Item
          hidden
          name={['customFields', id, 'dataTypeId']}
          initialValue={dataTypeId}
        />
        {dataTypeId === CustomFieldDataType.Text.id && (
          <CIMSFormItem {...formItem}>
            <Input {...input} />
          </CIMSFormItem>
        )}
        {dataTypeId === CustomFieldDataType.Boolean.id && (
          <CIMSFormItem {...formItem}>
            <Checkbox disabled={disabled} />
          </CIMSFormItem>
        )}
        {dataTypeId === CustomFieldDataType.Date.id && (
          <>
            <Form.Item
              hidden
              name={['customFields', id, 'timestamp']}
              initialValue={metadata?.timestamp}
            />
            <CIMSFormItem {...formItem}>
              <DatePicker
                {...input}
                showTime={metadata.timestamp ?? undefined}
                use12Hours
              />
            </CIMSFormItem>
          </>
        )}
        {dataTypeId === CustomFieldDataType.Number.id && (
          <CIMSFormItem {...formItem}>
            <InputNumber style={{ width: '100%' }} {...input} />
          </CIMSFormItem>
        )}
        {dataTypeId === CustomFieldDataType.List.id && (
          <>
            <Form.Item
              hidden
              name={['customFields', id, 'styleId']}
              initialValue={metadata?.styleId}
            />
            {metadata.styleId === CustomListItemStyle.Checkboxes.id ? (
              <CIMSFormItem {...formItem}>
                <Checkbox.Group
                  disabled={disabled}
                  options={customListItemOptions}
                />
              </CIMSFormItem>
            ) : (
              <CIMSFormItem {...formItem}>
                <CIMSSelect
                  {...input}
                  multiple={
                    metadata?.styleId === CustomListItemStyle.Multiple.id
                  }
                  options={options}
                />
              </CIMSFormItem>
            )}
          </>
        )}
        {dataTypeId === CustomFieldDataType.Entity.id && (
          <MetadataListFormItems
            disabled={disabledOptions}
            inputKey="entityId"
            canEdit={!disabled}
            customField={customField}
          />
        )}
        {dataTypeId === CustomFieldDataType.Report.id && (
          <MetadataListFormItems
            disabled={disabledOptions}
            inputKey="reportId"
            canEdit={!disabled}
            customField={customField}
          />
        )}
      </Col>
    </Row>
  )
}

export default CustomFieldRow
